import { useCallback, useState } from 'react';
import { uploadApi, UPLOAD_FOLDERS } from '@noor/eats-api';
import type { ProductFormImage } from './schema';

/**
 * Resolve a gallery of picked photos to durable storage keys.
 *
 * `FormField.ImageGrid` and `CoverPhotoField` hand back `ProductImage`s whose
 * `url` is a `blob:` object URL and whose `file` holds the bytes. A `blob:` URL
 * is per-document and dies with the tab, so submitting one would persist a link
 * that 404s on the next page load. This uploads every entry carrying a `file`
 * through the presign flow and swaps in the returned storage KEY — what the
 * product write endpoints take (the backend expands it to an absolute URL on
 * read). Entries without a `file` are already-saved values and pass through
 * untouched.
 *
 * Every freshly-picked photo is presigned in ONE call (`uploadApi.uploadMany`,
 * batched at the gateway's limit) and the PUTs then run concurrently against
 * object storage. A single failure rejects the whole batch, leaving the form
 * intact so the user can retry rather than saving a product with some photos
 * silently missing.
 *
 * Gallery ORDER is preserved: keys come back in the order the files went out,
 * and they are spliced back into the positions their entries occupied. That
 * matters now that position carries meaning — the product's cover is the first
 * photo, and the write path replays this order through the reorder endpoint.
 */
export function useUploadProductImages() {
  const [isUploading, setIsUploading] = useState(false);

  const uploadImages = useCallback(
    async (images: ProductFormImage[] | undefined): Promise<ProductFormImage[]> => {
      if (!images || images.length === 0) return [];

      // Entries with a `file` are the new picks; the rest are already stored
      // (or already uploaded) and pass through untouched.
      const pending = images.filter((image) => image.file);
      if (pending.length === 0) return images;

      setIsUploading(true);
      try {
        const keys = await uploadApi.uploadMany(
          // Non-null: `pending` is exactly the entries that carry a `file`.
          pending.map((image) => image.file!),
          UPLOAD_FOLDERS.product,
        );

        const keyByEntryId = new Map(
          pending.map((image, index) => [image.id, keys[index]]),
        );
        return images.map((image) => {
          const key = keyByEntryId.get(image.id);
          if (key === undefined) return image;
          // Drop `file` once uploaded so a re-submit can't upload it twice.
          // `saved` stays unset: the entry now holds a KEY, so unlike an
          // already-stored photo it is exactly what the write must send.
          const uploaded = { ...image, url: key };
          delete uploaded.file;
          return uploaded;
        });
      } finally {
        setIsUploading(false);
      }
    },
    [],
  );

  return { uploadImages, isUploading };
}
